import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Pagination } from '@/components/Pagination';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { contactsService, Contact, ContactGroupChip, ListResponse } from '@/services/contacts.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { savedFiltersService, SavedFilter } from '@/services/saved-filters.service';
import { Search, SearchX, Plus, Upload, Users, Bookmark, BookmarkPlus, X, Phone, Mail, LayoutGrid, List, Pencil, Trash2, FolderPlus, Tags, ShieldOff } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadErrorState } from '@/components/ListStates';
import { ContactGroupsModal } from '@/components/contacts/ContactGroupsModal';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useLabels } from '@/contexts/WorkspaceContext';

function InitialsChip({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .trim()
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const sz = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-8 w-8 text-xs';
  return (
    <div className={clsx('flex shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary font-semibold', sz)}>
      {initials || '?'}
    </div>
  );
}

// No country column — the phone already carries its dialling prefix.
const ROW_COLS = '28px minmax(0,2fr) 140px minmax(0,1.6fr) 76px 96px';

function GroupChips({ groups, emptyLabel }: { groups?: ContactGroupChip[]; emptyLabel?: string }) {
  if (!groups?.length) {
    return emptyLabel ? (
      <span className="inline-flex items-center gap-1 text-xs text-text-muted opacity-0 transition-opacity group-hover/g:opacity-100">
        <FolderPlus size={12} /> {emptyLabel}
      </span>
    ) : <span className="text-xs text-text-muted">No groups</span>;
  }
  const shown = groups.slice(0, 2);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {shown.map((g) => (
        <span
          key={g.groupUid}
          className="inline-flex max-w-[140px] items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold"
          style={{ background: `${g.color}18`, color: g.color, borderColor: `${g.color}30` }}
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: g.color }} />
          <span className="truncate">{g.name}</span>
        </span>
      ))}
      {groups.length > shown.length && (
        <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[11px] font-semibold text-text-muted" title={groups.slice(2).map((g) => g.name).join(', ')}>
          +{groups.length - shown.length}
        </span>
      )}
    </span>
  );
}

function ContactsEmpty({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  const t = useLabels();
  const plural = t('contacts').toLowerCase();
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
        {filtered ? <SearchX size={20} /> : <Users size={20} />}
      </div>
      <div>
        <p className="text-sm font-semibold text-text-primary">{filtered ? 'Nothing matches your filters' : `No ${plural} yet`}</p>
        <p className="mx-auto mt-1 max-w-sm text-xs text-text-muted">
          {filtered
            ? 'Try a different search or group, or clear the filters to see everyone.'
            : `Add ${plural} one at a time or import a CSV. Their WhatsApp consent and groups are managed here.`}
        </p>
      </div>
      {filtered ? (
        <Button variant="secondary" size="sm" onClick={onClear}>Clear filters</Button>
      ) : (
        <div className="flex flex-wrap justify-center gap-2">
          <Link to="/contacts/new"><Button size="sm" iconLeft={<Plus size={14} />}>New {t('contacts', 'singular').toLowerCase()}</Button></Link>
          <Link to="/contacts/import"><Button size="sm" variant="secondary" iconLeft={<Upload size={14} />}>Import CSV</Button></Link>
        </div>
      )}
    </div>
  );
}

const ICON_BTN = 'flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary';

function RowActions({ c, onGroups, onDelete }: { c: Contact; onGroups: () => void; onDelete: () => void }) {
  return (
    <span className="flex items-center justify-end gap-0.5">
      <Link to={`/contacts/${c.contactUid}?tab=details`} className={ICON_BTN} title="Edit" aria-label={`Edit ${c.firstName}`}>
        <Pencil size={14} />
      </Link>
      <button onClick={onGroups} className={ICON_BTN} title="Groups" aria-label={`Groups for ${c.firstName}`}>
        <FolderPlus size={14} />
      </button>
      <button onClick={onDelete} className={clsx(ICON_BTN, 'hover:bg-danger-soft hover:text-danger')} title="Delete" aria-label={`Delete ${c.firstName}`}>
        <Trash2 size={14} />
      </button>
    </span>
  );
}

export default function ContactsList() {
  const t = useLabels();
  const [data, setData] = useState<ListResponse | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [q, setQ] = useState('');
  // Contacts render as rows or cards; the choice sticks per device.
  const [view, setView] = useState<'list' | 'grid'>(
    () => (localStorage.getItem('contacts_view') === 'grid' ? 'grid' : 'list'),
  );
  const switchView = (v: 'list' | 'grid') => { setView(v); localStorage.setItem('contacts_view', v); };

  const [page, setPage] = useState(1);
  const [groupFilter, setGroupFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<'tags' | 'meta-share'>('tags');
  // Contacts whose groups / deletion are being edited — one row or the whole selection.
  const [groupsFor, setGroupsFor] = useState<Contact[] | null>(null);
  const [deleteFor, setDeleteFor] = useState<Contact[] | null>(null);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkMetaOptOut, setBulkMetaOptOut] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [activeFilterUid, setActiveFilterUid] = useState<string | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [saveSmart, setSaveSmart] = useState(true);
  const [smartCounts, setSmartCounts] = useState<Record<string, number | null>>({});

  const refresh = () => {
    setLoadError(false);
    return contactsService.list({ q: q || undefined, groupUid: groupFilter || undefined, page, limit: 25 })
      .then(setData)
      .catch(() => setLoadError(true));
  };

  const loadSavedFilters = () =>
    savedFiltersService.list('contacts').then(setSavedFilters).catch(() => undefined);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, page, groupFilter]);
  useEffect(() => { contactGroupsService.list().then(setGroups); }, []);
  useEffect(() => { loadSavedFilters(); }, []);

  // Re-evaluate smart-list counts whenever the filter set changes or contacts
  // are mutated. Backend caches each filter for 30s, so polling is cheap.
  useEffect(() => {
    const smartUids = savedFilters.filter((f) => f.isSmart).map((f) => f.filterUid);
    if (smartUids.length === 0) { setSmartCounts({}); return; }
    let alive = true;
    savedFiltersService.evaluateCounts(smartUids).then((m) => { if (alive) setSmartCounts(m); }).catch(() => undefined);
    return () => { alive = false; };
  }, [savedFilters, data?.total]);

  const applySavedFilter = (f: SavedFilter) => {
    const flt = (f.filter ?? {}) as { q?: string; groupUid?: string };
    setQ(flt.q ?? '');
    setGroupFilter(flt.groupUid ?? '');
    setPage(1);
    setActiveFilterUid(f.filterUid);
  };

  const clearSavedFilter = () => {
    setActiveFilterUid(null);
    setQ('');
    setGroupFilter('');
    setPage(1);
  };

  const onSaveFilter = async () => {
    if (!saveName.trim()) return;
    try {
      const created = await savedFiltersService.create({
        scope: 'contacts',
        name: saveName.trim(),
        filter: {
          ...(q ? { q } : {}),
          ...(groupFilter ? { groupUid: groupFilter } : {}),
        },
        isShared: saveShared,
        isSmart: saveSmart,
      });
      setSavedFilters((prev) => [created, ...prev]);
      setActiveFilterUid(created.filterUid);
      setSaveOpen(false);
      setSaveName('');
      setSaveShared(false);
      toast.success('View saved');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save view';
      toast.error(msg);
    }
  };

  const onDeleteFilter = async (uid: string) => {
    try {
      await savedFiltersService.remove(uid);
      setSavedFilters((prev) => prev.filter((f) => f.filterUid !== uid));
      if (activeFilterUid === uid) setActiveFilterUid(null);
    } catch {
      toast.error('Failed to delete view');
    }
  };

  const hasActiveFilter = !!q || !!groupFilter;

  const toggle = (uid: string) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };
  const pageUids = data?.items.map((c) => c.contactUid) ?? [];
  const allOnPage = pageUids.length > 0 && pageUids.every((u) => selected.has(u));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allOnPage) pageUids.forEach((u) => next.delete(u)); else pageUids.forEach((u) => next.add(u));
    setSelected(next);
  };
  // Selection can span pages; actions need the rows we still have.
  const selectedRows = (data?.items ?? []).filter((c) => selected.has(c.contactUid));
  const refreshGroups = () => contactGroupsService.list().then(setGroups).catch(() => undefined);

  const confirmDelete = async () => {
    const rows = deleteFor ?? [];
    setDeleteFor(null);
    if (rows.length === 0) return;
    try {
      if (rows.length === 1) await contactsService.remove(rows[0].contactUid);
      else await contactsService.bulkDelete(rows.map((c) => c.contactUid));
      toast.success(rows.length === 1 ? 'Contact deleted' : `${rows.length} contacts deleted`);
      setSelected((prev) => { const next = new Set(prev); rows.forEach((c) => next.delete(c.contactUid)); return next; });
      refresh();
      refreshGroups();
    } catch { /* interceptor toasts */ }
  };

  const bulkApplyTags = async () => {
    const tags = bulkTagInput.split(',').map((t) => t.trim()).filter(Boolean);
    if (!tags.length) { toast.error('Enter at least one tag'); return; }
    const r = await contactsService.bulkAddTags(Array.from(selected), tags);
    toast.success(`Tagged ${r.updated} contact${r.updated === 1 ? '' : 's'}`);
    setSelected(new Set());
    setBulkOpen(false);
    setBulkTagInput('');
    refresh();
  };

  const bulkApplyMetaShare = async () => {
    if (bulkMetaOptOut && !window.confirm(
      `Opt ${selected.size} contact${selected.size === 1 ? '' : 's'} out of Meta sharing?\n\n` +
      `Their PII will also be DELETED from every Custom Audience they're in. ` +
      `Required for GDPR / CCPA. This can't be undone by flipping the toggle back ` +
      `— you'd need to re-sync the source group.`
    )) return;
    setBulkBusy(true);
    try {
      const r = await contactsService.bulkSetMetaShareOptedOut(Array.from(selected), bulkMetaOptOut);
      toast.success(
        bulkMetaOptOut
          ? `Opted ${r.updated} out + ${r.propagated} Meta deletion${r.propagated === 1 ? '' : 's'} queued`
          : `Opted ${r.updated} contact${r.updated === 1 ? '' : 's'} back in`
      );
      setSelected(new Set());
      setBulkOpen(false);
      refresh();
    } finally { setBulkBusy(false); }
  };

  const bulkRemoveTagsAction = async () => {
    const tags = bulkTagInput.split(',').map((t) => t.trim()).filter(Boolean);
    if (!tags.length) { toast.error('Enter at least one tag'); return; }
    const r = await contactsService.bulkRemoveTags(Array.from(selected), tags);
    toast.success(`Untagged ${r.updated} contact${r.updated === 1 ? '' : 's'}`);
    setSelected(new Set());
    setBulkOpen(false);
    setBulkTagInput('');
    refresh();
  };

  return (
    <>
      <Topbar
        title={t('contacts')}
        breadcrumb="Audience"
        description={`Manage your ${t('contacts').toLowerCase()} and WhatsApp opt-ins.`}
        right={
          <div className="flex items-center gap-2">
            <Link to="/contacts/import">
              <Button variant="secondary" iconLeft={<Upload size={14} />}>Import CSV</Button>
            </Link>
            <Link to="/contacts/new">
              <Button iconLeft={<Plus size={14} />}>New {t('contacts', 'singular').toLowerCase()}</Button>
            </Link>
          </div>
        }
      />

      <div className="px-7 py-7 w-full">
        {/* Smart lists / saved views */}
        {(savedFilters.length > 0 || hasActiveFilter) && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-text-muted">
              <Bookmark size={12} /> Saved views
            </span>
            {savedFilters.map((f) => (
              <span
                key={f.filterUid}
                className={clsx(
                  'group inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition',
                  activeFilterUid === f.filterUid
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt cursor-pointer',
                )}
              >
                <button onClick={() => applySavedFilter(f)} className="inline-flex items-center gap-1.5">
                  {f.name}
                  {f.isShared && <Users size={10} className="opacity-60" />}
                  {/* Live count badge — only renders for smart filters once
                      the bulk-counts call returns a number. Mostly silent,
                      stays out of the way for static views. */}
                  {f.isSmart && typeof smartCounts[f.filterUid] === 'number' && (
                    <span className="ml-0.5 rounded-full bg-primary/20 px-1.5 py-0.5 text-2xs font-mono">
                      {smartCounts[f.filterUid]}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => onDeleteFilter(f.filterUid)}
                  className="opacity-0 group-hover:opacity-100 hover:text-danger transition"
                  aria-label="Delete view"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
            {hasActiveFilter && !activeFilterUid && (
              <button
                onClick={() => setSaveOpen(true)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/50 bg-primary-soft/40 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary-soft"
              >
                <BookmarkPlus size={12} /> Save current view
              </button>
            )}
            {activeFilterUid && (
              <button
                onClick={clearSavedFilter}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted hover:bg-surface-alt"
              >
                <X size={11} /> Clear view
              </button>
            )}
          </div>
        )}

        {/* Search + group filter */}
        <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="flex flex-1 items-center gap-2">
            {/* Input styles the inner <input>, so the width lives on a wrapper or it collapses to its icon. */}
            <div className="min-w-0 max-w-sm flex-1">
              <Input
                iconLeft={<Search size={15} />}
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by name, phone, email…"
              />
            </div>
            {/* List vs grid — sits next to the search so it reads as a view control. */}
            <div className="ml-auto flex shrink-0 items-center gap-1 rounded-lg border border-border bg-surface-alt/60 p-1 sm:ml-0">
              {([
                { v: 'list' as const, icon: <List size={14} />, label: 'List view' },
                { v: 'grid' as const, icon: <LayoutGrid size={14} />, label: 'Grid view' },
              ]).map((o) => (
                <button
                  key={o.v}
                  onClick={() => switchView(o.v)}
                  title={o.label}
                  aria-label={o.label}
                  aria-pressed={view === o.v}
                  className={clsx(
                    'rounded-md px-2 py-1 transition-colors',
                    view === o.v ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                  )}
                >
                  {o.icon}
                </button>
              ))}
            </div>
          </div>
          <div className="flex min-w-0 gap-2 overflow-x-auto pb-0.5">
            <button
              onClick={() => { setGroupFilter(''); setPage(1); }}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                !groupFilter
                  ? 'border-primary bg-primary-soft text-primary'
                  : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
            >
              All groups
            </button>
            {groups.map((g) => (
              <button
                key={g.groupUid}
                onClick={() => { setGroupFilter(g.groupUid); setPage(1); }}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                  groupFilter === g.groupUid
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: g.color }} />
                {g.name}
              </button>
            ))}
          </div>
        </div>

        {/* Selection actions — visible the moment a box is ticked. */}
        {selected.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 px-3 py-2">
            <span className="pl-1 text-sm font-semibold text-text-primary">{selected.size} selected</span>
            <span className="mx-1 h-4 w-px bg-border" />
            <Button size="sm" variant="secondary" iconLeft={<FolderPlus size={13} />} disabled={!selectedRows.length} onClick={() => setGroupsFor(selectedRows)}>Groups</Button>
            <Button size="sm" variant="secondary" iconLeft={<Tags size={13} />} onClick={() => { setBulkMode('tags'); setBulkOpen(true); }}>Tags</Button>
            <Button size="sm" variant="secondary" iconLeft={<ShieldOff size={13} />} onClick={() => { setBulkMode('meta-share'); setBulkOpen(true); }}>Meta sharing</Button>
            <Button size="sm" variant="danger" iconLeft={<Trash2 size={13} />} disabled={!selectedRows.length} onClick={() => setDeleteFor(selectedRows)}>Delete</Button>
            <button onClick={() => setSelected(new Set())} className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-text-muted hover:bg-surface hover:text-text-primary">
              <X size={12} /> Clear
            </button>
          </div>
        )}

        {/* Table */}
        {loadError ? (
          <LoadErrorState what={`your ${t('contacts').toLowerCase()}`} onRetry={() => void refresh()} />
        ) : !data ? (
          <Card padded={false} className="overflow-hidden">
            <div className="divide-y divide-border">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="skeleton h-8 w-8 rounded-full" />
                  <div className="skeleton h-4 w-40 rounded" />
                  <div className="skeleton h-4 w-28 rounded ml-auto" />
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <>
            {view === 'grid' ? (
              data.items.length === 0 ? (
                <Card padded={false}>
                  <ContactsEmpty filtered={hasActiveFilter} onClear={clearSavedFilter} />
                </Card>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {data.items.map((c) => (
                    <Card
                      key={c.contactUid}
                      className={clsx(
                        'relative transition-all hover:border-primary hover:shadow-soft',
                        selected.has(c.contactUid) && 'border-primary ring-1 ring-primary/30',
                      )}
                    >
                      {/* Selection stays available in card view so bulk actions work in both. */}
                      <input
                        type="checkbox"
                        checked={selected.has(c.contactUid)}
                        onChange={() => toggle(c.contactUid)}
                        className="absolute right-3 top-3 h-4 w-4 rounded border-border accent-primary"
                        aria-label={`Select ${c.firstName}`}
                      />
                      <Link to={`/contacts/${c.contactUid}`} className="group block">
                        <div className="flex items-center gap-3">
                          <InitialsChip name={`${c.firstName} ${c.lastName ?? ''}`} />
                          <div className="min-w-0 flex-1 pr-6">
                            <div className="truncate font-semibold text-text-primary transition-colors group-hover:text-primary">
                              {c.firstName} {c.lastName ?? ''}
                            </div>
                            {c.company && <div className="truncate text-xs text-text-muted">{c.company}</div>}
                          </div>
                        </div>
                        <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-xs">
                          <div className="flex items-center gap-1.5 text-text-secondary">
                            <Phone size={11} className="shrink-0 text-text-muted" />
                            <span className="truncate font-mono">{c.phone}</span>
                          </div>
                          {c.email && (
                            <div className="flex items-center gap-1.5 text-text-secondary">
                              <Mail size={11} className="shrink-0 text-text-muted" />
                              <span className="truncate">{c.email}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-1">
                            <span className="text-text-muted">{c.countryCode}</span>
                            {c.optedIn && c.whatsappConsentAt ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 font-semibold text-success">
                                <span className="h-1.5 w-1.5 rounded-full bg-success" /> Consent recorded
                              </span>
                            ) : (
                              <span className="text-text-muted">Not opted in</span>
                            )}
                          </div>
                        </div>
                      </Link>
                      <div className="mt-3 flex items-center gap-2 border-t border-border pt-2.5">
                        <div className="min-w-0 flex-1"><GroupChips groups={c.groups} /></div>
                        <RowActions c={c} onGroups={() => setGroupsFor([c])} onDelete={() => setDeleteFor([c])} />
                      </div>
                    </Card>
                  ))}
                </div>
              )
            ) : (
            <Card padded={false} className="overflow-hidden">
              {/* Narrow screens scroll the table sideways instead of crushing names and groups. */}
              <div className="overflow-x-auto"><div className="min-w-[680px]">
              <div
                className="grid items-center border-b border-border bg-surface-alt px-5 py-2.5"
                style={{ gridTemplateColumns: ROW_COLS, gap: '12px' }}
              >
                <input
                  type="checkbox"
                  checked={allOnPage}
                  onChange={toggleAll}
                  disabled={pageUids.length === 0}
                  className="h-4 w-4 rounded border-border accent-primary"
                  aria-label="Select all on this page"
                />
                {['Name', 'Phone', 'Groups', 'Consent', 'Actions'].map((h, i) => (
                  <span key={h} className={clsx('text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted', i === 4 && 'text-right')}>{h}</span>
                ))}
              </div>

              {data.items.length === 0 ? (
                <ContactsEmpty filtered={hasActiveFilter} onClear={clearSavedFilter} />
              ) : (
                <div className="divide-y divide-border">
                  {data.items.map((c) => (
                    <div
                      key={c.contactUid}
                      className={clsx('grid items-center px-5 py-3 text-sm transition-colors hover:bg-surface-alt/60', selected.has(c.contactUid) && 'bg-primary-soft/30')}
                      style={{ gridTemplateColumns: ROW_COLS, gap: '12px' }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(c.contactUid)}
                        onChange={() => toggle(c.contactUid)}
                        className="h-4 w-4 rounded border-border text-primary accent-primary"
                        aria-label={`Select ${c.firstName}`}
                      />
                      <Link to={`/contacts/${c.contactUid}`} className="group flex items-center gap-2.5 min-w-0">
                        <InitialsChip name={`${c.firstName} ${c.lastName ?? ''}`} />
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-text-primary group-hover:text-primary transition-colors">
                            {c.firstName} {c.lastName ?? ''}
                          </div>
                          {c.email && (
                            <div className="truncate text-[11px] text-text-muted">{c.email}</div>
                          )}
                        </div>
                      </Link>
                      <span className="truncate font-mono text-xs text-text-secondary">{c.phone}</span>
                      <button onClick={() => setGroupsFor([c])} className="group/g min-w-0 text-left" title="Manage groups">
                        <GroupChips groups={c.groups} emptyLabel="Add to group" />
                      </button>
                      <span>
                        {c.optedIn && c.whatsappConsentAt ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success">
                            <span className="h-1.5 w-1.5 rounded-full bg-success" /> Yes
                          </span>
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </span>
                      <RowActions c={c} onGroups={() => setGroupsFor([c])} onDelete={() => setDeleteFor([c])} />
                    </div>
                  ))}
                </div>
              )}
              </div></div>
            </Card>
            )}

            <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
          </>
        )}
      </div>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Bulk edit ${selected.size} contact${selected.size === 1 ? '' : 's'}`}>
        <div className="mb-3 flex gap-1 rounded-lg border border-border bg-surface-alt/40 p-1 text-xs">
          <button
            onClick={() => setBulkMode('tags')}
            className={clsx(
              'flex-1 rounded-md px-3 py-1.5 font-semibold transition',
              bulkMode === 'tags' ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted',
            )}
          >Manage tags</button>
          <button
            onClick={() => setBulkMode('meta-share')}
            className={clsx(
              'flex-1 rounded-md px-3 py-1.5 font-semibold transition',
              bulkMode === 'meta-share' ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted',
            )}
          >Meta sharing</button>
        </div>

        {bulkMode === 'tags' ? (
          <>
            <Input
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
              placeholder="vip, newsletter, qualified"
              autoFocus
            />
            <p className="mt-1.5 text-2xs text-text-muted">Comma-separated. Add or remove from all selected contacts.</p>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button variant="ghost" disabled={!bulkTagInput.trim()} onClick={bulkRemoveTagsAction}>Remove tags</Button>
              <Button disabled={!bulkTagInput.trim()} onClick={bulkApplyTags}>Add tags</Button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-xs text-text-secondary">
                Control whether these contacts' email + phone can be uploaded to Meta Custom Audiences
                for Facebook/Instagram ad targeting. Use this when a contact requests data deletion
                under GDPR / CCPA, or when they explicitly opt out of advertising channels.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBulkMetaOptOut(true)}
                  className={clsx(
                    'flex-1 rounded-lg border px-4 py-3 text-left transition-all',
                    bulkMetaOptOut ? 'border-danger bg-danger-soft' : 'border-border bg-surface hover:bg-surface-alt',
                  )}
                >
                  <p className={clsx('text-sm font-semibold', bulkMetaOptOut ? 'text-danger' : 'text-text-primary')}>
                    Opt out of Meta sharing
                  </p>
                  <p className="mt-0.5 text-2xs text-text-muted">
                    Sets <code className="font-mono">metaShareOptedOut=true</code> AND fires Meta delete
                    for every audience containing these contacts. Use for GDPR erasure requests.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setBulkMetaOptOut(false)}
                  className={clsx(
                    'flex-1 rounded-lg border px-4 py-3 text-left transition-all',
                    !bulkMetaOptOut ? 'border-primary bg-primary-soft' : 'border-border bg-surface hover:bg-surface-alt',
                  )}
                >
                  <p className={clsx('text-sm font-semibold', !bulkMetaOptOut ? 'text-primary' : 'text-text-primary')}>
                    Opt back in
                  </p>
                  <p className="mt-0.5 text-2xs text-text-muted">
                    Clears the opt-out flag. Doesn't auto-re-upload — run the audience sync separately
                    if you want these contacts back in your audiences.
                  </p>
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button
                variant={bulkMetaOptOut ? 'danger' : 'primary'}
                loading={bulkBusy}
                onClick={bulkApplyMetaShare}
              >
                {bulkMetaOptOut ? 'Opt out + delete from Meta' : 'Opt back in'}
              </Button>
            </div>
          </>
        )}
      </Modal>

      <ContactGroupsModal
        open={!!groupsFor}
        onClose={() => setGroupsFor(null)}
        targets={groupsFor ?? []}
        groups={groups}
        onGroupCreated={(g) => setGroups((prev) => [...prev, g])}
        onSaved={() => { refresh(); refreshGroups(); }}
      />

      <ConfirmDialog
        open={!!deleteFor}
        danger
        title={deleteFor && deleteFor.length > 1 ? `Delete ${deleteFor.length} contacts?` : 'Delete contact?'}
        message={
          deleteFor && deleteFor.length === 1
            ? `${deleteFor[0].firstName} ${deleteFor[0].lastName ?? ''} will be removed, along with their group memberships. Meta audiences are notified to drop them.`
            : 'They will be removed, along with their group memberships. Meta audiences are notified to drop them.'
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteFor(null)}
      />

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save current view">
        <div className="space-y-3">
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="View name (e.g. ‘UAE customers — opted in’)"
            autoFocus
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={saveShared}
              onChange={(e) => setSaveShared(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Share with my team
          </label>
          <label className="flex items-start gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={saveSmart}
              onChange={(e) => setSaveSmart(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              Smart list — auto-evaluate count
              <span className="block text-2xs text-text-muted">A live count appears next to the chip; refreshes every 30s.</span>
            </span>
          </label>
          <div className="text-xs text-text-muted bg-surface-alt/50 rounded-md px-3 py-2">
            Captures: {q ? <span className="font-mono">q="{q}"</span> : <span>(no search)</span>}
            {q && groupFilter ? ', ' : ''}
            {groupFilter ? <span className="font-mono">group={groups.find((g) => g.groupUid === groupFilter)?.name ?? groupFilter}</span> : null}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button disabled={!saveName.trim()} onClick={onSaveFilter}>Save view</Button>
        </div>
      </Modal>
    </>
  );
}
